import { useEffect, useState } from "react";
import { SharingModal } from "./SharingModal";
import { Access, AccessUpdateRequest } from "../../../access/models/Access";
import { observer } from "mobx-react";
import { t } from "i18next";
import { publicRoutes } from "../../../../App";
import {
  deleteAccess,
  postAccess,
  putAccess,
} from "../../../access/services/AccessApi";
import { SharingStatus } from "../../../access/models/SharingStatus";
import { LifeCycleStage } from "../../../osaway/models/LifeCycleStage";
import { ProjectApi } from "../../../project/services/ProjectApi";
import { useStores } from "../../stores/RootStore";
import { toast } from "../CustomToaster";

interface Props {
  onClose: () => any;
}
const ProjectSharingModalBase: React.FC<Props> = ({ onClose }) => {
  const { osaWayStore, projectStore } = useStores();
  const [sharingStatus, setSharingStatus] = useState<SharingStatus>();
  const [accesses, setAccesses] = useState<Access[]>([]);

  const project = projectStore.getSelected()!;

  const toggleSharing = async () => {
    const noWaysCompleted = () => {
      return !osaWayStore.ways.find(
        (way) => way.lifeCycleStage === LifeCycleStage.Completed
      );
    };

    const request = { isShared: !sharingStatus!.isShared };
    if (request.isShared && noWaysCompleted()) {
      toast("error", t("shared:shareModal.toast.noCompletedRoutes"));
      return;
    }
    const response = await ProjectApi.updateSharingStatus(project.id!, request);
    if (request.isShared) {
      toast("success", t("shared:shareModal.toast.accessUpdated"));
    } else {
      toast("success", t("shared:shareModal.toast.accessRevoked"));
    }
    setSharingStatus(response.data);
  };

  useEffect(() => {
    ProjectApi.getSharingStatus(project.id!)
      .then(({ data }) => setSharingStatus(data))
      .catch(onClose);
    ProjectApi.listAccesses(project.id!)
      .then(({ data }) => setAccesses(data))
      .catch(onClose);
    // eslint-disable-next-line
  }, [project]);

  if (!sharingStatus) return null;

  const addAccess = async (email: string) =>
    await postAccess({ email, roles: [], projectId: project.id! }).then(
      (response) => {
        setAccesses((prev) => [...prev, response.data]);
        return response.data;
      }
    );

  const updateAccess = async (accessId: string, data: AccessUpdateRequest) => {
    await putAccess(accessId, data).then((response) => {
      setAccesses((prev) =>
        prev.map((access) => (access.id === accessId ? response.data : access))
      );
      toast("success", t("shared:shareModal.toast.accessUpdated"));
    });
  };

  const removeAccess = async (accessId: string) => {
    await deleteAccess(accessId).then(() => {
      setAccesses((prev) => prev.filter((access) => access.id !== accessId));
      toast("success", t("shared:shareModal.toast.accessRevoked"));
    });
  };

  return (
    <SharingModal
      name={`${project.city}: ${project.name}`}
      publicRoute={publicRoutes.project(project.id)}
      isShared={sharingStatus.isShared}
      password={sharingStatus.password}
      accesses={accesses}
      onClose={onClose}
      toggleSharing={toggleSharing}
      addAccess={addAccess}
      updateAccess={updateAccess}
      removeAccess={removeAccess}
    />
  );
};

export const ProjectSharingModal = observer(ProjectSharingModalBase);
